import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../../lib/supabase';
import { logger } from '../../utils/logger';
import { tableKeys } from './tableKeys';
import { 
  TableDefinitionData,
  TableColumnData,
  TableRowData,
  TableCellData,
  formatTableDefinition,
  formatTableColumn,
  formatTableRow,
  formatTableCell,
  CompleteTableData,
  CreateTableDefinitionInput,
  CreateTableColumnInput,
  UpdateTableColumnInput,
  CreateTableRowInput,
  UpdateTableRowInput,
  UpdateTableCellInput,
  isCompleteTableData,
  FormattedTableColumn,
  FormattedTableRow,
  FormattedTableCell
} from './tableTypes';
import { 
  SUPABASE_ERROR_CODES, 
  ERROR_PATTERNS, 
  ERROR_MESSAGES,
  isTableDefinitionData,
  isSupabaseError 
} from './tableConstants';
import { db } from '../../lib/offline-db';
import { queueOfflineOperation } from '../../lib/offline-sync';
import { getConnectionState } from '../../contexts/ConnectionStateContext';
import { useQueryErrorHandler } from '../useQueryErrorHandler';
import { invalidateQuery, safeUpdateQueryData } from '../../utils/queryInvalidationUtils';
import { secureApi } from '../../api/secure';

/**
 * Hook for fetching complete table data for a specific topic
 * Fetches the table definition, columns, rows, and cells in a single query
 */
export function useTableData(topicId: string | null, options: { createIfMissing?: boolean } = {}) {
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();
  const { createIfMissing = false } = options;

  return useQuery({
    queryKey: topicId ? tableKeys.tableDataByTopic(topicId) : tableKeys.tableData(),
    queryFn: async (): Promise<CompleteTableData> => {
      if (!topicId) {
        throw new Error('Topic ID is required');
      }

      const fetchId = Date.now().toString().slice(-4);
      logger.fetch(`Starting table data fetch #${fetchId} for topic ${topicId} (online: ${isOnline})`);
      const startTime = performance.now();

      try {
        // Attempt to get table definition first, from either server or IndexedDB
        let tableDefinition: TableDefinitionData | null = null;
        let tableDefError = null;

        if (isOnline) {
          // Try to get table definition from server
          // With the new unique constraint, there should only be one table definition per topic
          const { data, error } = await supabase
            .from('table_definitions')
            .select('*')
            .eq('topic_id', topicId)
            .single();

          if (error) {
            if (isSupabaseError(error) && error.code === SUPABASE_ERROR_CODES.NO_ROWS_FOUND) {
              // No table definition found - this is expected for new topics
              logger.fetch('No table definition found for topic', { topicId });
              tableDefError = error;
            } else {
              // Actual error
              logger.error('Error fetching table definition:', error);
              tableDefError = error;
            }
          } else if (data && isTableDefinitionData(data)) {
            tableDefinition = data;

            // Store in IndexedDB for offline access
            await db.transaction('rw', db.tables, async () => {
              await db.table('tableDefinitions').put(tableDefinition);
            }).catch(e => {
              logger.error('Failed to store table definition in IndexedDB:', e);
              // Continue even if storage fails - this is non-critical
            });
          }
        } else {
          // If offline, try to get from IndexedDB
          try {
            const existingDefinitions = await db.transaction('r', db.tables, async () => {
              return db.table('tableDefinitions')
                .where('topic_id')
                .equals(topicId)
                .and(def => !def.isDeleted)
                .toArray();
            });

            if (existingDefinitions && existingDefinitions.length > 0) {
              // If multiple definitions, use most recent
              if (existingDefinitions.length > 1) {
                existingDefinitions.sort((a, b) => 
                  new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
                );
              }
              tableDefinition = existingDefinitions[0];
            }
          } catch (dbError) {
            logger.error('Error fetching table definition from IndexedDB:', dbError);
            // We'll handle this case below with createIfMissing
          }
        }

        // If no table definition and createIfMissing is true, create one
        if (!tableDefinition && createIfMissing) {
          if (isOnline) {
            // Use the Edge Function to create one
            try {
              logger.fetch('Creating new table definition via Edge Function');
              const result = await secureApi.createTableDefinition(topicId);

              if (result?.success && result?.table_definition) {
                tableDefinition = result.table_definition;
                
                // Store in IndexedDB
                await db.transaction('rw', db.tables, async () => {
                  await db.table('tableDefinitions').put(tableDefinition);
                }).catch(e => {
                  logger.error('Failed to store new table definition in IndexedDB:', e);
                  // Continue even if storage fails
                });
              } else {
                throw new Error('Invalid response from create-table-definition function');
              }
            } catch (createError) {
              const errorMessage = createError instanceof Error ? createError.message : 'Unknown error';
              logger.error('Error creating table definition:', createError);
              
              // Check for specific error types and provide user-friendly messages
              if (ERROR_PATTERNS.UNIQUE_CONSTRAINT.test(errorMessage)) {
                throw new Error(ERROR_MESSAGES.TABLE_ALREADY_EXISTS);
              } else if (ERROR_PATTERNS.PERMISSION_DENIED.test(errorMessage)) {
                throw new Error(ERROR_MESSAGES.PERMISSION_DENIED);
              } else if (ERROR_PATTERNS.NETWORK_ERROR.test(errorMessage)) {
                throw new Error(ERROR_MESSAGES.NETWORK_ERROR);
              } else {
                throw new Error(`${ERROR_MESSAGES.GENERIC_CREATE_ERROR}: ${errorMessage}`);
              }
            }
          } else {
            // If offline, DON'T create a table definition
            // This prevents conflicts when multiple offline sessions create tables for the same topic
            logger.fetch('Offline: Cannot create table definition while offline');
            throw new Error(ERROR_MESSAGES.OFFLINE_TABLE_CREATION);
          }
        } else if (tableDefError && (!isSupabaseError(tableDefError) || tableDefError.code !== SUPABASE_ERROR_CODES.NO_ROWS_FOUND)) {
          // Handle errors that aren't just "no rows returned"
          logger.error('Table definition fetch error:', tableDefError);
          throw tableDefError;
        }

        // Initialize empty arrays for the response data
        let columns: TableColumnData[] = [];
        let rows: TableRowData[] = [];
        let cells: TableCellData[] = [];

        // If we have a table definition, fetch related data
        if (tableDefinition) {
          const tableDefId = tableDefinition.id;

          if (isOnline) {
            // Fetch columns and rows in parallel
            const [columnsResult, rowsResult] = await Promise.all([
              supabase
                .from('table_columns')
                .select('*')
                .eq('table_definition_id', tableDefId)
                .order('order_index'),

              supabase
                .from('table_rows')
                .select('*')
                .eq('table_definition_id', tableDefId)
                .order('order_index'),
            ]);

            if (columnsResult.error) throw columnsResult.error;
            if (rowsResult.error) throw rowsResult.error;

            columns = columnsResult.data || [];
            rows = rowsResult.data || [];

            // Store in IndexedDB for offline access
            await db.transaction('rw', db.tables, async () => {
              if (columns.length > 0) {
                await db.table('tableColumns').bulkPut(columns);
              }
              if (rows.length > 0) {
                await db.table('tableRows').bulkPut(rows);
              }
            }).catch(e => {
              logger.error('Failed to store columns and rows in IndexedDB:', e);
              // Continue even if storage fails
            });

            // If we have rows, fetch their cells
            if (rows.length > 0) {
              const rowIds = rows.map(row => row.id);
              const { data: cellsData, error: cellsError } = await supabase
                .from('table_cells')
                .select('*')
                .in('row_id', rowIds);

              if (cellsError) throw cellsError;
              cells = cellsData || [];

              // Store cells in IndexedDB
              if (cells.length > 0) {
                await db.transaction('rw', db.tables, async () => {
                  await db.table('tableCells').bulkPut(cells);
                }).catch(e => {
                  logger.error('Failed to store cells in IndexedDB:', e);
                  // Continue even if storage fails
                });
              }
            }
          } else {
            // If offline, fetch from IndexedDB
            try {
              const [offlineColumns, offlineRows] = await Promise.all([
                db.transaction('r', db.tables, () => 
                  db.table('tableColumns')
                    .where('table_definition_id')
                    .equals(tableDefId)
                    .and(col => !col.isDeleted)
                    .toArray()
                ),
                db.transaction('r', db.tables, () => 
                  db.table('tableRows')
                    .where('table_definition_id')
                    .equals(tableDefId)
                    .and(row => !row.isDeleted)
                    .toArray()
                )
              ]);

              columns = offlineColumns || [];
              rows = offlineRows || [];

              // Sort by order_index
              columns.sort((a, b) => a.order_index - b.order_index);
              rows.sort((a, b) => a.order_index - b.order_index);

              // If we have rows, fetch their cells
              if (rows.length > 0) {
                const rowIds = rows.map(row => row.id);
                const offlineCells = await db.transaction('r', db.tables, () => 
                  db.table('tableCells')
                    .where('row_id')
                    .anyOf(rowIds)
                    .and(cell => !cell.isDeleted)
                    .toArray()
                );

                cells = offlineCells || [];
              }
            } catch (offlineError) {
              logger.error('Error fetching offline table data:', offlineError);
              // Continue with empty arrays rather than failing completely
            }
          }
        }

        // Format the data for the UI
        const formattedData: CompleteTableData = {
          tableDefinition: tableDefinition ? formatTableDefinition(tableDefinition) : null,
          columns: columns.map(col => formatTableColumn(col)),
          rows: rows.map(row => formatTableRow(row)),
          cells: cells.map(cell => formatTableCell(cell)),
        };

        // Log performance metrics
        const endTime = performance.now();
        const duration = endTime - startTime;
        logger.fetch(`Table data fetch #${fetchId} completed in ${duration.toFixed(2)}ms`, {
          definitionFound: !!formattedData.tableDefinition,
          columnCount: formattedData.columns.length,
          rowCount: formattedData.rows.length,
          cellCount: formattedData.cells.length,
        });

        return formattedData;
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { 
            fetchId,
            operation: 'fetchTableData',
            topicId,
            isOnline
          }
        });
        
        throw appError;
      }
    },
    enabled: !!topicId,
  });
}

/**
 * Hook for creating a table definition
 */
export function useCreateTableDefinition() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (input: CreateTableDefinitionInput) => {
      const { topicId, name = 'Table' } = input;
      
      // If offline, create a temporary definition locally
      if (!isOnline) {
        try {
          const tempId = crypto.randomUUID();
          const now = new Date().toISOString();
          
          const tableDefinition: TableDefinitionData = {
            id: tempId,
            topic_id: topicId,
            name,
            created_at: now,
            updated_at: now,
          };
          
          // Store in IndexedDB
          await db.transaction('rw', db.tables, async () => {
            await db.table('tableDefinitions').put(tableDefinition);
          });
          
          // Queue for sync when online
          await queueOfflineOperation('tableDefinitions', 'create', tempId, tableDefinition);
          
          return formatTableDefinition(tableDefinition);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'createTableDefinition', topicId, mode: 'offline' }
          });
          throw error;
        }
      }
      
      // If online, use the Edge Function to create the table definition
      try {
        logger.fetch('Creating new table definition via Edge Function');
        const result = await secureApi.createTableDefinition(topicId);

        if (!result?.success || !result?.table_definition) {
          throw new Error('Invalid response from create-table-definition function');
        }
        
        const tableDefinition = result.table_definition;
        
        // Store in IndexedDB for offline access
        await db.transaction('rw', db.tables, async () => {
          await db.table('tableDefinitions').put(tableDefinition);
        }).catch(e => {
          logger.error('Failed to store new table definition in IndexedDB:', e);
          // Continue even if storage fails
        });
        
        return formatTableDefinition(tableDefinition);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'createTableDefinition', topicId }
        });
        throw appError;
      }
    },
    
    onSuccess: (tableDefinition, variables) => {
      // Invalidate any queries for this topic's table data
      invalidateQuery(queryClient, tableKeys.tableDataByTopic(variables.topicId), {
        refetchType: 'active',
        context: { operation: 'createTableDefinition' }
      });
      
      // Update any queries for this specific table definition
      safeUpdateQueryData(queryClient, tableKeys.definitionDetail(tableDefinition.id), () => tableDefinition, {
        context: { operation: 'createTableDefinition' }
      });
    }
  });
}

/**
 * Hook for adding a new column to a table
 */
export function useAddTableColumn() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    onMutate: async (input: CreateTableColumnInput) => {
      const { 
        tableDefinitionId, 
        name = 'New Column', 
        type = 'text', 
        isVisible = true,
        orderIndex
      } = input;
      
      // Create optimistic column
      const optimisticColumn: FormattedTableColumn = {
        id: `temp-${crypto.randomUUID()}`,
        tableDefinitionId,
        name,
        type,
        orderIndex: orderIndex || 10000,
        isVisible,
        options: input.options,
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Find the table data that contains this table definition
      // First, try to find the topic ID by checking cached table data queries
      let topicId: string | undefined;
      let previousData: CompleteTableData | undefined;
      let targetQueryKey: readonly unknown[] | undefined;
      
      // Get all table data queries to find the one containing our table definition
      const tableDataQueries = queryClient.getQueriesData<CompleteTableData>({ 
        queryKey: [...tableKeys.tableData()] 
      });
      
      for (const [queryKey, data] of tableDataQueries) {
        if (isCompleteTableData(data) && data.tableDefinition?.id === tableDefinitionId) {
          previousData = data;
          targetQueryKey = queryKey;
          
          // Extract topicId from query key if it has the structure we expect
          if (Array.isArray(queryKey) && queryKey.length >= 3) {
            const lastElement = queryKey[queryKey.length - 1];
            if (typeof lastElement === 'object' && 'topicId' in lastElement) {
              topicId = lastElement.topicId;
            }
          }
          break;
        }
      }
      
      if (previousData && targetQueryKey) {
        // Update with optimistic data
        const updatedData: CompleteTableData = {
          ...previousData,
          columns: [...previousData.columns, optimisticColumn].sort((a, b) => a.orderIndex - b.orderIndex)
        };
        
        queryClient.setQueryData(targetQueryKey, updatedData);
      }
      
      return { previousData, optimisticColumn, targetQueryKey, topicId };
    },
    
    mutationFn: async (input: CreateTableColumnInput) => {
      const { 
        tableDefinitionId, 
        name = 'New Column', 
        type = 'text', 
        isVisible = true, 
        afterColumnId
      } = input;
      
      // Calculate new order index
      let orderIndex = input.orderIndex;
      if (orderIndex === undefined) {
        // If not provided, we need to calculate it based on existing columns
        try {
          // Get existing columns to calculate position
          const existingColumns = await db.transaction('r', db.tables, () => 
            db.table('tableColumns')
              .where('table_definition_id')
              .equals(tableDefinitionId)
              .and(col => !col.isDeleted)
              .toArray()
          );
          
          // Sort by position
          existingColumns.sort((a, b) => a.order_index - b.order_index);
          
          if (afterColumnId) {
            // Find where to position after the specified column
            const afterColumn = existingColumns.find(col => col.id === afterColumnId);
            
            if (afterColumn) {
              // Find the next column after this one
              const afterIndex = existingColumns.findIndex(col => col.id === afterColumnId);
              const nextColumn = afterIndex < existingColumns.length - 1 ? existingColumns[afterIndex + 1] : null;
              
              if (nextColumn) {
                // Position between the two columns
                orderIndex = afterColumn.order_index + ((nextColumn.order_index - afterColumn.order_index) / 2);
              } else {
                // Position after the last column
                orderIndex = afterColumn.order_index + 1000;
              }
            } else {
              // If specified column not found, add at the end
              orderIndex = existingColumns.length > 0 
                ? existingColumns[existingColumns.length - 1].order_index + 1000
                : 1000;
            }
          } else {
            // If no afterColumnId, add at the end
            orderIndex = existingColumns.length > 0 
              ? existingColumns[existingColumns.length - 1].order_index + 1000
              : 1000;
          }
        } catch (error) {
          logger.error('Error calculating column order index:', error);
          // Default to a high value if calculation fails
          orderIndex = 10000;
        }
      }
      
      // If offline, create locally and queue for sync
      if (!isOnline) {
        try {
          const columnId = crypto.randomUUID();
          const now = new Date().toISOString();
          
          const column: TableColumnData = {
            id: columnId,
            table_definition_id: tableDefinitionId,
            name,
            type,
            order_index: orderIndex,
            is_visible: isVisible,
            options: input.options,
            created_at: now,
            updated_at: now,
          };
          
          // Store in IndexedDB
          await db.transaction('rw', db.tables, async () => {
            await db.table('tableColumns').put(column);
          });
          
          // Queue for sync when online
          await queueOfflineOperation('tableColumns', 'create', columnId, column);
          
          return formatTableColumn(column);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'addTableColumn', tableDefinitionId, mode: 'offline' }
          });
          throw error;
        }
      }
      
      // If online, create in the database
      try {
        const { data, error } = await supabase
          .from('table_columns')
          .insert([{
            table_definition_id: tableDefinitionId,
            name,
            type,
            order_index: orderIndex,
            is_visible: isVisible,
            options: input.options
                }])
          .select()
          .single();
        
        if (error) throw error;
        
        // Store in IndexedDB for offline access
        await db.transaction('rw', db.tables, async () => {
          await db.table('tableColumns').put(data);
        }).catch(e => {
          logger.error('Failed to store column in IndexedDB:', e);
          // Continue even if storage fails
        });
        
        // Create empty cells for all existing rows
        try {
          const rows = await db.transaction('r', db.tables, () => 
            db.table('tableRows')
              .where('table_definition_id')
              .equals(tableDefinitionId)
              .and(row => !row.isDeleted)
              .toArray()
          );
          
          if (rows.length > 0) {
            const cellInserts = rows.map(row => ({
              row_id: row.id,
              column_id: data.id,
              value: ''
            }));
            
            const { data: cellsData, error: cellsError } = await supabase
              .from('table_cells')
              .insert(cellInserts)
              .select();
            
            if (cellsError) throw cellsError;
            
            // Store cells in IndexedDB
            if (cellsData && cellsData.length > 0) {
              await db.transaction('rw', db.tables, async () => {
                await db.table('tableCells').bulkPut(cellsData);
              }).catch(e => {
                logger.error('Failed to store cells in IndexedDB:', e);
                // Continue even if storage fails
              });
            }
          }
        } catch (cellsError) {
          logger.error('Error creating empty cells for new column:', cellsError);
          // Continue even if cell creation fails - they can be created on demand later
        }
        
        return formatTableColumn(data);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'addTableColumn', tableDefinitionId }
        });
        throw appError;
      }
    },
    
    onError: (error, variables, context) => {
      // Rollback the optimistic update on error
      if (context?.previousData && context?.targetQueryKey) {
        queryClient.setQueryData(context.targetQueryKey, context.previousData);
      }
    },
    
    onSuccess: (column, variables, context) => {
      // Invalidate queries for the table's columns
      invalidateQuery(queryClient, tableKeys.columnsByTableDef(variables.tableDefinitionId), {
        refetchType: 'active',
        context: { operation: 'addTableColumn' }
      });
      
      // Update the column detail query
      safeUpdateQueryData(queryClient, tableKeys.columnDetail(column.id), () => column, {
        context: { operation: 'addTableColumn' }
      });
      
      // If we have the target query key and topic ID, update and invalidate efficiently
      if (context?.targetQueryKey) {
        // Replace the optimistic column with the real one
        if (context.optimisticColumn && context.previousData) {
          const updatedData: CompleteTableData = {
            ...context.previousData,
            columns: context.previousData.columns
              .map(col => col.id === context.optimisticColumn.id ? column : col)
              .sort((a, b) => a.orderIndex - b.orderIndex)
          };
          
          queryClient.setQueryData(context.targetQueryKey, updatedData);
        }
        
        // Invalidate to ensure fresh data
        invalidateQuery(queryClient, context.targetQueryKey, {
          refetchType: 'active',
          context: { operation: 'addTableColumn' }
        });
      } else {
        // Fallback: invalidate by topic ID if we found it
        if (context?.topicId) {
          invalidateQuery(queryClient, tableKeys.tableDataByTopic(context.topicId), {
            refetchType: 'active',
            context: { operation: 'addTableColumn' }
          });
        }
      }
    }
  });
}

/**
 * Hook for updating a table column
 */
export function useUpdateTableColumn() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ 
      columnId, 
      updates 
    }: { 
      columnId: string;
      updates: UpdateTableColumnInput;
    }) => {
      if (!columnId) {
        throw new Error('Column ID is required');
      }
      
      const now = new Date().toISOString();
      
      // Create updates object with only the fields that are provided
      const updatedFields: Record<string, unknown> = {
        updated_at: now,
      };
      
      if (updates.name !== undefined) updatedFields.name = updates.name;
      if (updates.type !== undefined) updatedFields.type = updates.type;
      if (updates.orderIndex !== undefined) updatedFields.order_index = updates.orderIndex;
      if (updates.width !== undefined) updatedFields.width = updates.width;
      if (updates.isVisible !== undefined) updatedFields.is_visible = updates.isVisible;
      if (updates.options !== undefined) updatedFields.options = updates.options;
      
      
      // If offline, update locally and queue for sync
      if (!isOnline) {
        try {
          // Get current column from IndexedDB
          const currentColumn = await db.transaction('r', db.tables, async () => {
            return db.table('tableColumns').get(columnId);
          });
          
          if (!currentColumn || currentColumn.isDeleted) {
            throw new Error(`Column ${columnId} not found in offline storage`);
          }
          
          // Merge updates with current data
          const updatedColumn = {
            ...currentColumn,
            ...updatedFields
          };
          
          // Update in IndexedDB
          await db.transaction('rw', db.tables, async () => {
            await db.table('tableColumns').put(updatedColumn);
          });
          
          // Queue for sync when online
          await queueOfflineOperation('tableColumns', 'update', columnId, updatedColumn);
          
          return formatTableColumn(updatedColumn);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateTableColumn', columnId, mode: 'offline' }
          });
          throw error;
        }
      }
      
      // If online, update in the database
      try {
        const { data, error } = await supabase
          .from('table_columns')
          .update(updatedFields)
          .eq('id', columnId)
          .select()
          .single();
        
        if (error) throw error;
        
        // Update in IndexedDB for offline access
        await db.transaction('rw', db.tables, async () => {
          // Get current data first to preserve fields not included in the update
          const existingColumn = await db.table('tableColumns').get(columnId);
          
          if (existingColumn) {
            await db.table('tableColumns').put({
              ...existingColumn,
              ...data
            });
          } else {
            // If we don't have it yet, just store the update
            await db.table('tableColumns').put(data);
          }
        }).catch(e => {
          logger.error('Failed to update column in IndexedDB:', e);
          // Continue even if storage fails
        });
        
        return formatTableColumn(data);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'updateTableColumn', columnId }
        });
        throw appError;
      }
    },
    
    onSuccess: (column, variables) => {
      // Update the column in the cache
      safeUpdateQueryData(queryClient, tableKeys.columnDetail(variables.columnId), () => column, {
        context: { operation: 'updateTableColumn', columnId: variables.columnId }
      });
      
      // Update the column in any column lists
      safeUpdateQueryData(queryClient, tableKeys.columnsByTableDef(column.tableDefinitionId), 
        (columns) => {
          if (!columns) return undefined;
          return columns.map(existingColumn => 
            existingColumn.id === column.id ? column : existingColumn
          );
        },
        { context: { operation: 'updateTableColumn', columnId: variables.columnId } }
      );
      
      // If this affects any table data queries, update those too
      const tableDataQueries = queryClient.getQueriesData({ 
        queryKey: tableKeys.tableData()
      });
      
      for (const [queryKey, data] of tableDataQueries) {
        if (data && typeof data === 'object' && 'columns' in data) {
          safeUpdateQueryData(queryClient, queryKey, (tableData: any) => {
            if (!tableData || !tableData.columns) return undefined;
            
            // Update the column in the tableData
            return {
              ...tableData,
              columns: tableData.columns.map((existingColumn: any) => 
                existingColumn.id === column.id ? column : existingColumn
              )
            };
          }, 
          { context: { operation: 'updateTableColumn', columnId: variables.columnId } });
        }
      }
    }
  });
}

/**
 * Hook for deleting a table column
 */
export function useDeleteTableColumn() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (columnId: string) => {
      if (!columnId) {
        throw new Error('Column ID is required');
      }
      
      // Get column data first for cache updates
      let column: TableColumnData | null = null;
      
      try {
        column = await db.transaction('r', db.tables, async () => {
          return db.table('tableColumns').get(columnId);
        });
        
        if (!column && isOnline) {
          // If not in IndexedDB, try to get from server
          const { data, error } = await supabase
            .from('table_columns')
            .select('*')
            .eq('id', columnId)
            .single();
            
          if (!error && data) {
            column = data;
          }
        }
        
        if (!column) {
          throw new Error(`Column ${columnId} not found`);
        }
        
        // Get the table definition ID for cache updates
        const tableDefinitionId = column.table_definition_id;
        
        // If offline, mark as deleted locally and queue for sync
        if (!isOnline) {
          // Mark as deleted in IndexedDB
          await db.transaction('rw', db.tables, async () => {
            // Mark column as deleted
            await db.table('tableColumns').update(columnId, { isDeleted: true });
            
            // Also mark related cells as deleted
            const cells = await db.table('tableCells')
              .where('column_id')
              .equals(columnId)
              .toArray();
              
            for (const cell of cells) {
              await db.table('tableCells').update(cell.id, { isDeleted: true });
              // Queue cell deletion
              await queueOfflineOperation('tableCells', 'delete', cell.id, { id: cell.id });
            }
          });
          
          // Queue column deletion
          await queueOfflineOperation('tableColumns', 'delete', columnId, { id: columnId });
          
          return { columnId, tableDefinitionId };
        }
        
        // If online, delete from database (cells are deleted via cascading constraints)
        const { error } = await supabase
          .from('table_columns')
          .delete()
          .eq('id', columnId);
          
        if (error) throw error;
        
        // Remove from IndexedDB
        await db.transaction('rw', db.tables, async () => {
          // Delete the column
          await db.table('tableColumns').delete(columnId);
          
          // Also delete related cells
          const cells = await db.table('tableCells')
            .where('column_id')
            .equals(columnId)
            .toArray();
            
          for (const cell of cells) {
            await db.table('tableCells').delete(cell.id);
          }
        }).catch(e => {
          logger.error('Failed to delete column from IndexedDB:', e);
          // Continue even if storage fails
        });
        
        return { columnId, tableDefinitionId };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'deleteTableColumn', columnId }
        });
        throw appError;
      }
    },
    
    onSuccess: (result) => {
      const { columnId, tableDefinitionId } = result;
      
      // Remove column from cache
      queryClient.removeQueries({
        queryKey: tableKeys.columnDetail(columnId),
        exact: true
      });
      
      // Update the columns list
      safeUpdateQueryData(queryClient, tableKeys.columnsByTableDef(tableDefinitionId),
        (columns) => {
          if (!columns) return undefined;
          return columns.filter(column => column.id !== columnId);
        },
        { context: { operation: 'deleteTableColumn', columnId } }
      );
      
      // Update any table data queries
      const tableDataQueries = queryClient.getQueriesData({
        queryKey: tableKeys.tableData()
      });
      
      for (const [queryKey, data] of tableDataQueries) {
        if (data && typeof data === 'object' && 'columns' in data && 'cells' in data) {
          safeUpdateQueryData(queryClient, queryKey, (tableData: any) => {
            if (!tableData) return undefined;
            
            return {
              ...tableData,
              // Remove the column
              columns: tableData.columns.filter((column: any) => column.id !== columnId),
              // Remove cells for this column
              cells: tableData.cells.filter((cell: any) => cell.columnId !== columnId)
            };
          },
          { context: { operation: 'deleteTableColumn', columnId } });
        }
      }
    }
  });
}

/**
 * Hook for adding a new row to a table
 */
export function useAddTableRow() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    onMutate: async (input: CreateTableRowInput) => {
      const { tableDefinitionId } = input;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: tableKeys.rowsByTableDef(tableDefinitionId) });
      
      // Create optimistic row
      const optimisticRow: FormattedTableRow = {
        id: crypto.randomUUID(),
        tableDefinitionId,
        orderIndex: input.orderIndex ?? 0, // Will be calculated properly if not provided
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Find the table data that contains this table definition
      let topicId: string | undefined;
      let previousData: CompleteTableData | undefined;
      let targetQueryKey: readonly unknown[] | undefined;
      
      // Get all table data queries to find the one containing our table definition
      const tableDataQueries = queryClient.getQueriesData<CompleteTableData>({ 
        queryKey: [...tableKeys.tableData()] 
      });
      
      for (const [queryKey, data] of tableDataQueries) {
        if (isCompleteTableData(data) && data.tableDefinition?.id === tableDefinitionId) {
          previousData = data;
          targetQueryKey = queryKey;
          
          // Extract topicId from query key if it has the structure we expect
          if (Array.isArray(queryKey) && queryKey.length >= 3) {
            const lastElement = queryKey[queryKey.length - 1];
            if (typeof lastElement === 'object' && 
                lastElement !== null && 
                'topicId' in lastElement) {
              const typedElement = lastElement as { topicId?: unknown };
              if (typeof typedElement.topicId === 'string') {
                topicId = typedElement.topicId;
              }
            }
          }
          break;
        }
      }
      
      // Update orderIndex if not provided
      if (input.orderIndex === undefined && previousData) {
        optimisticRow.orderIndex = previousData.rows.length;
      }
      
      // Create optimistic cells for all columns
      const optimisticCells: FormattedTableCell[] = previousData?.columns.map(column => ({
        id: crypto.randomUUID(),
        rowId: optimisticRow.id,
        columnId: column.id,
        value: '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      })) || [];
      
      if (previousData && targetQueryKey) {
        // Update with optimistic data
        const updatedData: CompleteTableData = {
          ...previousData,
          rows: [...previousData.rows, optimisticRow].sort((a, b) => a.orderIndex - b.orderIndex),
          cells: [...previousData.cells, ...optimisticCells]
        };
        
        queryClient.setQueryData(targetQueryKey, updatedData);
      }
      
      return { previousData, optimisticRow, optimisticCells, targetQueryKey, topicId };
    },
    
    mutationFn: async (input: CreateTableRowInput) => {
      const { tableDefinitionId } = input;
      
      // Calculate order index if not provided
      let orderIndex = input.orderIndex;
      if (orderIndex === undefined) {
        try {
          // Get existing rows to calculate position
          const existingRows = await db.transaction('r', db.tables, () => 
            db.table('tableRows')
              .where('table_definition_id')
              .equals(tableDefinitionId)
              .and(row => !row.isDeleted)
              .toArray()
          );
          
          // Use the existing row count as the order index, or 0 if no rows
          orderIndex = existingRows.length;
        } catch (error) {
          logger.error('Error calculating row order index:', error);
          // Default to 0 if calculation fails
          orderIndex = 0;
        }
      }
      
      // If offline, create locally and queue for sync
      if (!isOnline) {
        try {
          const rowId = crypto.randomUUID();
          const now = new Date().toISOString();
          
          const row: TableRowData = {
            id: rowId,
            table_definition_id: tableDefinitionId,
            order_index: orderIndex,
            created_at: now,
            updated_at: now,
          };
          
          // Store in IndexedDB
          await db.transaction('rw', db.tables, async () => {
            await db.table('tableRows').put(row);
            
            // Create empty cells for all columns
            const columns = await db.table('tableColumns')
              .where('table_definition_id')
              .equals(tableDefinitionId)
              .and(col => !col.isDeleted)
              .toArray();
              
            if (columns.length > 0) {
              const cells = columns.map(column => ({
                id: crypto.randomUUID(),
                row_id: rowId,
                column_id: column.id,
                value: '',
                created_at: now,
                updated_at: now,
              }));
              
              // Store cells
              for (const cell of cells) {
                await db.table('tableCells').put(cell);
                // Queue cell creation
                await queueOfflineOperation('tableCells', 'create', cell.id, cell);
              }
            }
          });
          
          // Queue row creation
          await queueOfflineOperation('tableRows', 'create', rowId, row);
          
          return formatTableRow(row);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'addTableRow', tableDefinitionId, mode: 'offline' }
          });
          throw error;
        }
      }
      
      // If online, create in the database
      try {
        const { data, error } = await supabase
          .from('table_rows')
          .insert([{
            table_definition_id: tableDefinitionId,
            order_index: orderIndex,
          }])
          .select()
          .single();
        
        if (error) throw error;
        
        // Store in IndexedDB for offline access
        await db.transaction('rw', db.tables, async () => {
          await db.table('tableRows').put(data);
        }).catch(e => {
          logger.error('Failed to store row in IndexedDB:', e);
          // Continue even if storage fails
        });
        
        // Create empty cells for all columns
        try {
          const { data: columns, error: columnsError } = await supabase
            .from('table_columns')
            .select('id')
            .eq('table_definition_id', tableDefinitionId);
          
          if (columnsError) throw columnsError;
          
          if (columns.length > 0) {
            const cellInserts = columns.map(column => ({
              row_id: data.id,
              column_id: column.id,
              value: ''
            }));
            
            const { data: cellsData, error: cellsError } = await supabase
              .from('table_cells')
              .insert(cellInserts)
              .select();
            
            if (cellsError) throw cellsError;
            
            // Store cells in IndexedDB
            if (cellsData && cellsData.length > 0) {
              await db.transaction('rw', db.tables, async () => {
                await db.table('tableCells').bulkPut(cellsData);
              }).catch(e => {
                logger.error('Failed to store cells in IndexedDB:', e);
                // Continue even if storage fails
              });
            }
          }
        } catch (cellsError) {
          logger.error('Error creating empty cells for new row:', cellsError);
          // Continue even if cell creation fails - they can be created on demand later
        }
        
        return formatTableRow(data);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'addTableRow', tableDefinitionId }
        });
        throw appError;
      }
    },
    
    onError: (err, variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousData && context?.targetQueryKey) {
        queryClient.setQueryData(context.targetQueryKey, context.previousData);
      }
    },
    
    onSuccess: (row, variables, context) => {
      // Replace optimistic data with actual data
      if (context?.targetQueryKey && context?.optimisticRow) {
        queryClient.setQueryData(context.targetQueryKey, (oldData: CompleteTableData | undefined) => {
          if (!oldData) return undefined;
          
          // Replace the optimistic row with the real row
          const updatedRows = oldData.rows.map(r => 
            r.id === context.optimisticRow.id ? row : r
          );
          
          // Remove optimistic cells and wait for refetch to get real cells
          const updatedCells = oldData.cells.filter(cell => 
            cell.rowId !== context.optimisticRow.id
          );
          
          return {
            ...oldData,
            rows: updatedRows,
            cells: updatedCells
          };
        });
      }
      
      // Update the row detail query
      safeUpdateQueryData(queryClient, tableKeys.rowDetail(row.id), () => row, {
        context: { operation: 'addTableRow' }
      });
    },
    
    onSettled: (data, error, variables, context) => {
      // Always refetch the complete table data to ensure consistency
      if (context?.topicId) {
        invalidateQuery(queryClient, tableKeys.tableDataByTopic(context.topicId), {
          refetchType: 'active',
          context: { operation: 'addTableRow' }
        });
      }
    }
  });
}

/**
 * Hook for updating a table row
 */
export function useUpdateTableRow() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async ({ 
      rowId, 
      updates 
    }: { 
      rowId: string;
      updates: UpdateTableRowInput;
    }) => {
      if (!rowId) {
        throw new Error('Row ID is required');
      }
      
      const now = new Date().toISOString();
      
      // Create updates object with only the fields that are provided
      const updatedFields: Record<string, unknown> = {
        updated_at: now,
      };
      
      if (updates.orderIndex !== undefined) updatedFields.order_index = updates.orderIndex;
      
      
      // If offline, update locally and queue for sync
      if (!isOnline) {
        try {
          // Get current row from IndexedDB
          const currentRow = await db.transaction('r', db.tables, async () => {
            return db.table('tableRows').get(rowId);
          });
          
          if (!currentRow || currentRow.isDeleted) {
            throw new Error(`Row ${rowId} not found in offline storage`);
          }
          
          // Merge updates with current data
          const updatedRow = {
            ...currentRow,
            ...updatedFields
          };
          
          // Update in IndexedDB
          await db.transaction('rw', db.tables, async () => {
            await db.table('tableRows').put(updatedRow);
          });
          
          // Queue for sync when online
          await queueOfflineOperation('tableRows', 'update', rowId, updatedRow);
          
          return formatTableRow(updatedRow);
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateTableRow', rowId, mode: 'offline' }
          });
          throw error;
        }
      }
      
      // If online, update in the database
      try {
        const { data, error } = await supabase
          .from('table_rows')
          .update(updatedFields)
          .eq('id', rowId)
          .select()
          .single();
        
        if (error) throw error;
        
        // Update in IndexedDB for offline access
        await db.transaction('rw', db.tables, async () => {
          // Get current data first to preserve fields not included in the update
          const existingRow = await db.table('tableRows').get(rowId);
          
          if (existingRow) {
            await db.table('tableRows').put({
              ...existingRow,
              ...data
            });
          } else {
            // If we don't have it yet, just store the update
            await db.table('tableRows').put(data);
          }
        }).catch(e => {
          logger.error('Failed to update row in IndexedDB:', e);
          // Continue even if storage fails
        });
        
        return formatTableRow(data);
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'updateTableRow', rowId }
        });
        throw appError;
      }
    },
    
    onSuccess: (row, variables) => {
      // Update the row in the cache
      safeUpdateQueryData(queryClient, tableKeys.rowDetail(variables.rowId), () => row, {
        context: { operation: 'updateTableRow', rowId: variables.rowId }
      });
      
      // Update the row in any row lists
      safeUpdateQueryData(queryClient, tableKeys.rowsByTableDef(row.tableDefinitionId), 
        (rows) => {
          if (!rows) return undefined;
          return rows.map(existingRow => 
            existingRow.id === row.id ? row : existingRow
          );
        },
        { context: { operation: 'updateTableRow', rowId: variables.rowId } }
      );
      
      // If this affects any table data queries, update those too
      const tableDataQueries = queryClient.getQueriesData({ 
        queryKey: tableKeys.tableData()
      });
      
      for (const [queryKey, data] of tableDataQueries) {
        if (data && typeof data === 'object' && 'rows' in data) {
          safeUpdateQueryData(queryClient, queryKey, (tableData: any) => {
            if (!tableData || !tableData.rows) return undefined;
            
            // Update the row in the tableData
            return {
              ...tableData,
              rows: tableData.rows.map((existingRow: any) => 
                existingRow.id === row.id ? row : existingRow
              )
            };
          }, 
          { context: { operation: 'updateTableRow', rowId: variables.rowId } });
        }
      }
    }
  });
}

/**
 * Hook for deleting a table row
 */
export function useDeleteTableRow() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    mutationFn: async (rowId: string) => {
      if (!rowId) {
        throw new Error('Row ID is required');
      }
      
      // Get row data first for cache updates
      let row: TableRowData | null = null;
      
      try {
        row = await db.transaction('r', db.tables, async () => {
          return db.table('tableRows').get(rowId);
        });
        
        if (!row && isOnline) {
          // If not in IndexedDB, try to get from server
          const { data, error } = await supabase
            .from('table_rows')
            .select('*')
            .eq('id', rowId)
            .single();
            
          if (!error && data) {
            row = data;
          }
        }
        
        if (!row) {
          throw new Error(`Row ${rowId} not found`);
        }
        
        // Get the table definition ID for cache updates
        const tableDefinitionId = row.table_definition_id;
        
        // If offline, mark as deleted locally and queue for sync
        if (!isOnline) {
          // Mark as deleted in IndexedDB
          await db.transaction('rw', db.tables, async () => {
            // Mark row as deleted
            await db.table('tableRows').update(rowId, { isDeleted: true });
            
            // Also mark related cells as deleted
            const cells = await db.table('tableCells')
              .where('row_id')
              .equals(rowId)
              .toArray();
              
            for (const cell of cells) {
              await db.table('tableCells').update(cell.id, { isDeleted: true });
              // Queue cell deletion
              await queueOfflineOperation('tableCells', 'delete', cell.id, { id: cell.id });
            }
          });
          
          // Queue row deletion
          await queueOfflineOperation('tableRows', 'delete', rowId, { id: rowId });
          
          return { rowId, tableDefinitionId };
        }
        
        // If online, delete from database (cells are deleted via cascading constraints)
        const { error } = await supabase
          .from('table_rows')
          .delete()
          .eq('id', rowId);
          
        if (error) throw error;
        
        // Remove from IndexedDB
        await db.transaction('rw', db.tables, async () => {
          // Delete the row
          await db.table('tableRows').delete(rowId);
          
          // Also delete related cells
          const cells = await db.table('tableCells')
            .where('row_id')
            .equals(rowId)
            .toArray();
            
          for (const cell of cells) {
            await db.table('tableCells').delete(cell.id);
          }
        }).catch(e => {
          logger.error('Failed to delete row from IndexedDB:', e);
          // Continue even if storage fails
        });
        
        return { rowId, tableDefinitionId };
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'deleteTableRow', rowId }
        });
        throw appError;
      }
    },
    
    onSuccess: (result) => {
      const { rowId, tableDefinitionId } = result;
      
      // Remove row from cache
      queryClient.removeQueries({
        queryKey: tableKeys.rowDetail(rowId),
        exact: true
      });
      
      // Update the rows list
      safeUpdateQueryData(queryClient, tableKeys.rowsByTableDef(tableDefinitionId),
        (rows) => {
          if (!rows) return undefined;
          return rows.filter(row => row.id !== rowId);
        },
        { context: { operation: 'deleteTableRow', rowId } }
      );
      
      // Invalidate cell queries for this row
      queryClient.invalidateQueries({
        queryKey: tableKeys.cellsByRow(rowId),
        exact: true
      });
      
      // Update any table data queries
      const tableDataQueries = queryClient.getQueriesData({
        queryKey: tableKeys.tableData()
      });
      
      for (const [queryKey, data] of tableDataQueries) {
        if (data && typeof data === 'object' && 'rows' in data && 'cells' in data) {
          safeUpdateQueryData(queryClient, queryKey, (tableData: any) => {
            if (!tableData) return undefined;
            
            return {
              ...tableData,
              // Remove the row
              rows: tableData.rows.filter((row: any) => row.id !== rowId),
              // Remove cells for this row
              cells: tableData.cells.filter((cell: any) => cell.rowId !== rowId)
            };
          },
          { context: { operation: 'deleteTableRow', rowId } });
        }
      }
    }
  });
}

/**
 * Hook for updating a table cell
 */
export function useUpdateTableCell() {
  const queryClient = useQueryClient();
  const connectionState = getConnectionState();
  const isOnline = connectionState?.isOnline ?? navigator.onLine;
  const { handleError } = useQueryErrorHandler();

  return useMutation({
    onMutate: async (input: UpdateTableCellInput) => {
      const { rowId, columnId, value } = input;
      
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({ queryKey: tableKeys.cellsByRow(rowId) });
      
      // Create optimistic cell
      const optimisticCell: FormattedTableCell = {
        id: `temp-${rowId}-${columnId}`, // Temporary ID for optimistic cell
        rowId,
        columnId,
        value: value || '',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString()
      };
      
      // Find the table data that contains this cell
      let previousData: CompleteTableData | undefined;
      let targetQueryKey: readonly unknown[] | undefined;
      
      // Get all table data queries to find the one containing our cell
      const tableDataQueries = queryClient.getQueriesData<CompleteTableData>({ 
        queryKey: [...tableKeys.tableData()] 
      });
      
      for (const [queryKey, data] of tableDataQueries) {
        if (isCompleteTableData(data)) {
          // Check if this table data contains our row
          const hasRow = data.rows.some(r => r.id === rowId);
          if (hasRow) {
            previousData = data;
            targetQueryKey = queryKey;
            break;
          }
        }
      }
      
      if (previousData && targetQueryKey) {
        // Find existing cell or add new one
        const existingCellIndex = previousData.cells.findIndex(
          c => c.rowId === rowId && c.columnId === columnId
        );
        
        let updatedCells: FormattedTableCell[];
        if (existingCellIndex >= 0) {
          // Update existing cell
          updatedCells = [...previousData.cells];
          updatedCells[existingCellIndex] = {
            ...updatedCells[existingCellIndex],
            value: value || '',
            updatedAt: new Date().toISOString()
          };
        } else {
          // Add new cell
          updatedCells = [...previousData.cells, optimisticCell];
        }
        
        const updatedData: CompleteTableData = {
          ...previousData,
          cells: updatedCells
        };
        
        queryClient.setQueryData(targetQueryKey, updatedData);
      }
      
      return { previousData, optimisticCell, targetQueryKey };
    },
    
    mutationFn: async (input: UpdateTableCellInput) => {
      const { rowId, columnId, value } = input;
      
      if (!rowId || !columnId) {
        throw new Error('Row ID and Column ID are required');
      }
      
      // Sanitize value to make sure it's not undefined
      const safeValue = value === undefined ? '' : value;
      const now = new Date().toISOString();
      
      // Check if the cell already exists
      let existingCell: TableCellData | null = null;
      
      try {
        existingCell = await db.transaction('r', db.tables, async () => {
          return db.table('tableCells')
            .where('[row_id+column_id]')
            .equals([rowId, columnId])
            .and(cell => !cell.isDeleted)
            .first();
        });
      } catch (findError) {
        logger.error('Error finding existing cell:', findError);
        // Continue without existing cell - we'll try to create it
      }
      
      // If offline, create/update locally and queue for sync
      if (!isOnline) {
        try {
          if (existingCell) {
            // Update existing cell
            const updatedCell = {
              ...existingCell,
              value: safeValue,
              updated_at: now
            };
            
            // Update in IndexedDB
            await db.transaction('rw', db.tables, async () => {
              await db.table('tableCells').put(updatedCell);
            });
            
            // Queue for sync when online
            await queueOfflineOperation('tableCells', 'update', existingCell.id, updatedCell);
            
            return formatTableCell(updatedCell);
          } else {
            // Create new cell
            const cellId = crypto.randomUUID();
            const newCell: TableCellData = {
              id: cellId,
              row_id: rowId,
              column_id: columnId,
              value: safeValue,
              created_at: now,
              updated_at: now
            };
            
            // Store in IndexedDB
            await db.transaction('rw', db.tables, async () => {
              await db.table('tableCells').put(newCell);
            });
            
            // Queue for sync when online
            await queueOfflineOperation('tableCells', 'create', cellId, newCell);
            
            return formatTableCell(newCell);
          }
        } catch (offlineError) {
          const error = handleError(offlineError, {
            action: 'toast',
            context: { operation: 'updateTableCell', rowId, columnId, mode: 'offline' }
          });
          throw error;
        }
      }
      
      // If online, create/update in the database
      try {
        if (existingCell) {
          // Update existing cell
          const { data, error } = await supabase
            .from('table_cells')
            .update({
              value: safeValue,
              updated_at: now
            })
            .eq('id', existingCell.id)
            .select()
            .single();
          
          if (error) throw error;
          
          // Update in IndexedDB for offline access
          await db.transaction('rw', db.tables, async () => {
            await db.table('tableCells').put(data);
          }).catch(e => {
            logger.error('Failed to update cell in IndexedDB:', e);
            // Continue even if storage fails
          });
          
          return formatTableCell(data);
        } else {
          // Create new cell
          const { data, error } = await supabase
            .from('table_cells')
            .insert([{
              row_id: rowId,
              column_id: columnId,
              value: safeValue
            }])
            .select()
            .single();
          
          if (error) throw error;
          
          // Store in IndexedDB for offline access
          await db.transaction('rw', db.tables, async () => {
            await db.table('tableCells').put(data);
          }).catch(e => {
            logger.error('Failed to store cell in IndexedDB:', e);
            // Continue even if storage fails
          });
          
          return formatTableCell(data);
        }
      } catch (error) {
        const appError = handleError(error, {
          action: 'toast',
          context: { operation: 'updateTableCell', rowId, columnId }
        });
        throw appError;
      }
    },
    
    onError: (err, variables, context) => {
      // Rollback optimistic update on error
      if (context?.previousData && context?.targetQueryKey) {
        queryClient.setQueryData(context.targetQueryKey, context.previousData);
      }
    },
    
    onSuccess: (cell, variables, context) => {
      // Replace optimistic data with actual data
      if (context?.targetQueryKey) {
        queryClient.setQueryData(context.targetQueryKey, (oldData: CompleteTableData | undefined) => {
          if (!oldData) return undefined;
          
          // Replace optimistic cell with real cell
          const updatedCells = oldData.cells.map(c => {
            if (c.rowId === cell.rowId && c.columnId === cell.columnId) {
              return cell;
            }
            return c;
          });
          
          // If the cell wasn't found (shouldn't happen), add it
          const cellExists = updatedCells.some(c => 
            c.rowId === cell.rowId && c.columnId === cell.columnId
          );
          
          if (!cellExists) {
            updatedCells.push(cell);
          }
          
          return {
            ...oldData,
            cells: updatedCells
          };
        });
      }
      
      // Update specific cell detail query
      if (cell.id) {
        safeUpdateQueryData(queryClient, tableKeys.cellDetail(cell.id), () => cell, {
          context: { operation: 'updateTableCell', rowId: variables.rowId, columnId: variables.columnId }
        });
      }
    },
    
    onSettled: (data, error, variables) => {
      // Always refetch cells for this row to ensure consistency
      invalidateQuery(queryClient, tableKeys.cellsByRow(variables.rowId), {
        refetchType: 'active',
        context: { operation: 'updateTableCell' }
      });
    }
  });
}